// Finance calculations — pure functions over a NORMALIZED config
// (see normalizeConfig.js) and the Finance Data months map.
//
// Module reads:
//   drawdownPool.monthlyAccrual   — monthly accrual credited to the pool
//   recurringSessions.rolloverValue — credit per missed session
//   recurringSessions.sessions    — the standing session list for a month

export function getCurrentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function getMonthsBetween(start, end) {
  const months = [];
  if (!start || !end) return months;
  let [y, m] = start.split("-").map(Number);
  const [ey, em] = end.split("-").map(Number);
  while (y < ey || (y === ey && m <= em)) {
    months.push(`${y}-${String(m).padStart(2, "0")}`);
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return months;
}

export function addMonths(ym, n) {
  let [y, m] = ym.split("-").map(Number);
  m += n;
  while (m > 12) { m -= 12; y++; }
  while (m < 1) { m += 12; y--; }
  return `${y}-${String(m).padStart(2, "0")}`;
}

// The months the ledger shows: engagement start through the current month,
// plus `ledgerLookAhead` future months (so sessions and spend can be planned
// ahead). Future months are projections and never feed the live balance.
export function ledgerMonths(cfg, currentMonth) {
  const ahead = Math.max(0, Number(cfg.ledgerLookAhead) || 0);
  return getMonthsBetween(cfg.engagementStart || currentMonth, addMonths(currentMonth, ahead));
}

export function formatMonth(ym) {
  const [y, m] = ym.split("-");
  const names = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(m)]} ${y}`;
}

// The standing roster for a month: active entries whose from/to window (if
// any) covers the month. Paused, opted-out and ended people get no row.
export function rosterForMonth(cfg, month) {
  const sessions = cfg.modules.recurringSessions?.sessions || [];
  return sessions.filter(c => {
    if (c.status && c.status !== "active") return false;
    if (month && c.from && c.from > month) return false;
    if (month && c.to && c.to < month) return false;
    return true;
  });
}

// Seats mode: people above the baseline. A roster entry with `from` is an
// addition billed on top of the retainer's seat counts every month from
// `from` until `to` (or until the next Schedule A folds them into the
// baseline, at which point `from` is removed). Each costs its type's seat
// rate (`seats[].rate`) or its own `rate`. Optional, display only:
// `started` ("YYYY-MM") when the person actually started, if that is earlier
// than the first month billed (`from`); `detail` for what the line is when
// it is not a new seat ("extra session per month").
export function rosterAdditionsForMonth(cfg, month) {
  const rs = cfg.modules.recurringSessions || {};
  const seatRate = Object.fromEntries((rs.seats || []).map(s => [s.type, s.rate || 0]));
  return (rs.sessions || [])
    .filter(c => c.from && c.from <= month && (!c.to || c.to >= month) && (!c.status || c.status === "active"))
    .map(c => ({ ...c, monthly: c.rate ?? seatRate[c.type] ?? 0, isNew: c.from === month }));
}

// Joiners and leavers for a month, from the roster's from/to windows.
export function rosterChangesForMonth(cfg, month) {
  const sessions = cfg.modules.recurringSessions?.sessions || [];
  return {
    added: sessions.filter(c => c.from === month && (!c.status || c.status === "active")),
    removed: sessions.filter(c => c.to === month),
  };
}

// What the retainer should be billed at for a month: the baseline plus the
// additions rolling on. Compare against the invoice that funds the month.
export function expectedRetainerForMonth(cfg, month) {
  const base = cfg.modules.retainer?.monthlyTotal || 0;
  const additions = rosterAdditionsForMonth(cfg, month).reduce((t, c) => t + c.monthly, 0);
  return { base, additions, total: Math.round((base + additions) * 100) / 100 };
}

export function getDefaultMonthData(cfg, month) {
  if (cfg.modules.recurringSessions?.mode === "seats") return { coaching: [], lineItems: [] };
  return { coaching: rosterForMonth(cfg, month).map(c => ({ id: c.id, label: c.label, happened: true, ...(c.type ? { type: c.type } : {}) })), lineItems: [] };
}

export function getMonthData(cfg, finance, month) {
  return finance[month] || getDefaultMonthData(cfg, month);
}

export function calcMonth(cfg, finance, month) {
  const md = getMonthData(cfg, finance, month);
  // Standing sessions that didn't happen roll over; one-offs are extras and don't
  const missedSessions = md.coaching.filter(c => !c.happened && !c.oneOff).length;
  const coachingRollover = missedSessions * (cfg.modules.recurringSessions?.rolloverValue || 0);
  // Line items drawn from the pool vs. one-off work billed separately on
  // completion (`billedSeparately: true`) — the latter never touches the balance.
  const lineItemsTotal = md.lineItems.filter(li => !li.billedSeparately).reduce((sum, li) => sum + li.amount, 0);
  const separateTotal = md.lineItems.filter(li => li.billedSeparately).reduce((sum, li) => sum + li.amount, 0);
  return { coachingRollover, lineItemsTotal, separateTotal, drawdown: cfg.modules.drawdownPool?.monthlyAccrual || 0, data: md };
}

// Everything still to be invoiced separately, across the ledger months
// (line items with `billedSeparately`) and the block-client `extras[]`.
// Status defaults to "to-invoice" when an item carries none.
export function toInvoiceSummary(finance, extras) {
  const items = [];
  Object.values(finance || {}).forEach(md => (md.lineItems || []).forEach(li => { if (li.billedSeparately) items.push(li); }));
  (extras || []).forEach(x => items.push(x));
  const open = items.filter(x => (x.status || "to-invoice") === "to-invoice");
  return { count: open.length, total: Math.round(open.reduce((t, x) => t + (Number(x.amount) || 0), 0) * 100) / 100 };
}

// sessionBlock: delivered is DERIVED from confirmed sessions, never typed.
// Cancelled sessions do not burn a slot. Status is "last" at one remaining
// (raise rebooking at the next session) and "complete" at zero.
//
// Blocks over time: Finance Config holds the CURRENT block (standing);
// Finance Data holds its sessions plus a `history` of completed blocks, each
// carrying its own sessions. A session may carry `block`; sessions without
// it belong to the current block (backwards compatible).
export const BLOCK_SESSION_STATUSES = ["scheduled", "done", "cancelled"];

export function sessionsForBlock(block, sessions) {
  const no = block?.blockNo;
  return (sessions || []).filter(s => s.block === undefined || s.block === null || no === undefined || s.block === no);
}

// Money works like the drawdown pool, consumed per session. The block's
// `value` (ex GST; or `rate` x purchased) is what the block is worth; each
// delivered session draws down value / purchased; cancelled sessions draw
// nothing. Funding comes from `invoices[]` — { number, date, sessions,
// amount, status: paid | sent } — because a block can be billed in tranches
// (Amalga Block 2: 3 sessions in Aug, 3 later). Prepaid balance is what's
// been PAID minus what's been drawn; `toInvoice` is value not yet invoiced;
// `owing` is delivery that has outrun payment. A block with no invoices[]
// falls back to the whole value as the balance and the legacy `invoice`
// string for its state.
export function calcSessionBlock(block, sessions) {
  const purchased = block?.purchased || 0;
  const list = sessionsForBlock(block, sessions);
  const delivered = list.filter(s => s.status === "done").length;
  const remaining = Math.max(purchased - delivered, 0);
  const status = remaining === 0 ? "complete" : remaining === 1 ? "last" : "open";
  const value = block?.value || (block?.rate && purchased ? block.rate * purchased : 0);
  const perSession = value && purchased ? value / purchased : 0;
  const drawn = Math.min(delivered, purchased) * perSession;
  const invoices = Array.isArray(block?.invoices) ? block.invoices : [];
  const funded = invoices.filter(i => i.status === "paid").reduce((t, i) => t + (i.amount || 0), 0);
  const invoiced = invoices.reduce((t, i) => t + (i.amount || 0), 0);
  const balance = invoices.length ? Math.max(funded - drawn, 0) : Math.max(value - drawn, 0);
  const toInvoice = invoices.length ? Math.max(value - invoiced, 0) : 0;
  const owing = invoices.length ? Math.max(drawn - funded, 0) : 0;
  const fundingState = !invoices.length ? (block?.invoice || "")
    : invoiced < value ? "part" : funded < invoiced ? "invoiced" : "paid";
  return { purchased, delivered, remaining, status, value, perSession, drawn, invoices, funded, invoiced, balance, toInvoice, owing, fundingState };
}

// A completed block from history: { blockNo, purchased, label, opened, closed, sessions }
export function summarizeBlock(entry) {
  const sessions = entry.sessions || [];
  const delivered = sessions.filter(s => s.status === "done").length;
  const dates = sessions.filter(s => s.status === "done" && s.date).map(s => s.date).sort();
  const money = calcSessionBlock(entry, sessions);
  return { blockNo: entry.blockNo, purchased: entry.purchased || 0, delivered, value: money.value, invoice: money.fundingState, invoices: money.invoices,
    opened: entry.opened || dates[0] || "", closed: entry.closed || dates[dates.length - 1] || "" };
}

export function sortSessionsByDate(sessions) {
  return [...(sessions || [])].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return a.date.localeCompare(b.date);
  });
}

export function calcCumulativeBalance(cfg, finance, upToMonth) {
  const months = getMonthsBetween(cfg.engagementStart || getCurrentMonth(), upToMonth);
  let balance = Number(cfg.modules.drawdownPool?.openingBalance) || 0;
  months.forEach(m => { const mc = calcMonth(cfg, finance, m); balance += mc.drawdown + mc.coachingRollover - mc.lineItemsTotal; });
  return Math.round(balance * 100) / 100;
}

// retainer.invoices[]: { number, date, month: "YYYY-MM" (the month the
// retainer covers — invoiced a month ahead), amount (ex GST), status }.
// Returns the invoice for a month, or null if the module tracks invoices and
// none has been raised for it. undefined = module does not track invoices.
export function retainerInvoiceFor(cfg, month) {
  const invoices = cfg.modules.retainer?.invoices;
  if (!Array.isArray(invoices)) return undefined;
  return invoices.find(i => i.month === month) || null;
}
